import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { Avatar } from "@/components/AppHeader";
import { ReportCard, PostCard } from "@/components/Cards";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Search, ChevronLeft } from "lucide-react";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState({ reports: [], posts: [], users: [] });
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState(q);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    api.get("/search", { params: { q } })
      .then((r) => setResults(r.data))
      .catch((e) => console.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <MobileShell
      topBar={
        <div className="h-14 flex items-center gap-2 px-3">
          <button onClick={() => navigate(-1)} className="p-1 rounded-full hover:bg-[var(--te-input-bg)]"><ChevronLeft size={20} /></button>
          <form onSubmit={(e) => { e.preventDefault(); setParams({ q: input }); }} className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--te-text-muted)]" />
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search…" className="te-input pl-9 py-2 text-sm" data-testid="search-page-input" autoFocus />
          </form>
        </div>
      }
    >
      <div className="p-3">
        {!q ? (
          <p className="text-[var(--te-text-muted)] text-center py-10 text-sm">Enter a search term above.</p>
        ) : loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
        ) : (
          <div className="space-y-5">
            {results.users.length > 0 && (
              <section>
                <h2 className="te-eyebrow mb-2 px-1">People · {results.users.length}</h2>
                <div className="space-y-2">
                  {results.users.map((u) => (
                    <Link to={`/profile/${u.id}`} key={u.id} className="te-card p-3 flex items-center gap-3" data-testid={`search-user-${u.id}`}>
                      <Avatar user={u} size={40} />
                      <div>
                        <div className="text-sm font-medium text-[var(--te-text)]">{u.name}</div>
                        <div className="text-xs text-[var(--te-text-muted)]">{u.city_en || u.email}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {results.reports.length > 0 && (
              <section>
                <h2 className="te-eyebrow mb-2 px-1">Reports · {results.reports.length}</h2>
                <div className="space-y-3">{results.reports.map((r) => <ReportCard key={r.id} report={r} />)}</div>
              </section>
            )}
            {results.posts.length > 0 && (
              <section>
                <h2 className="te-eyebrow mb-2 px-1">Posts · {results.posts.length}</h2>
                <div className="space-y-3">{results.posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
              </section>
            )}
            {q && !loading && !results.users.length && !results.reports.length && !results.posts.length && (
              <p className="text-center text-sm text-[var(--te-text-muted)] py-10">No matches for &ldquo;{q}&rdquo;.</p>
            )}
          </div>
        )}
      </div>
    </MobileShell>
  );
}
